import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Confetti } from '@/components/ui/confetti';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, CreditCard, Loader2,
  UserCircle, RefreshCw, XCircle, Play,
  Users, Bell, BarChart3, History, LogOut, ArrowLeft, Trophy, Medal, GraduationCap, Gift, Award, UserPlus, FileText
} from 'lucide-react';
import { useSellerAchievements } from '@/hooks/useSellerAchievements';
import { AchievementUnlockedModal } from '@/components/membership/achievements/AchievementUnlockedModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import villatechBlueLogo from '@/assets/villatech-logo-blue.png';

import {
  QuickRedeemDialog,
  RenewMembershipDialog,
  AnimatedBackground,
  AdminStatsDashboard,
  QuickStatsBar,
  MemberFilters,
  ActivityTimeline,
  MemberDetailsPanel,
  ActionsPanel,
  ActivationDialog,
  SellerRankingDashboard,
  SellerRankingCard,
  ScoreSummaryTab,
  AchievementsScreen,
  PrizesScreen,
  AdminPrizeManager,
  PrizeWinnerNotification,
  MyDiscountPurchasesList,
  DiscountRemindersCard,
  TeamManagementSection,
  CreateTeamMemberForm,
  GameRulesEmbedded,
  benefitTypes,
  formatDate,
  getCurrentMonthYear,
  monthlyBenefits,
  getUsedBenefitsThisMonth,
  isNearExpiration,
  type MemberData,
  type Membership,
  type Redemption,
  type Payment,
  type DiscountPurchase,
} from '@/components/membership';
import { usePrizes } from '@/hooks/usePrizes';
import { TrainingSection } from '@/components/membership/training';
import { SellerTrainingView } from '@/components/membership/training/seller/SellerTrainingView';
import { PinKeypad } from '@/components/ui/pin-keypad';
import { PinDisplay } from '@/components/ui/pin-display';

const AdminCanjes: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isVendedor } = useAuth();
  const { toast } = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [allMembers, setAllMembers] = useState<MemberData[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('miembros');
  const [equipoSubTab, setEquipoSubTab] = useState<'ranking' | 'cursos' | 'crear'>('ranking');
  const [rankingSubTab, setRankingSubTab] = useState<'posiciones' | 'puntos' | 'premios' | 'logros' | 'reglas'>('posiciones');
  const [crearSubTab, setCrearSubTab] = useState<'equipo' | 'nuevo'>('equipo');

  // Set default tab based on role
  useEffect(() => {
    if (isAdmin) {
      setActiveTab('acciones');
    } else if (isVendedor) {
      setActiveTab('miembros');
    }
  }, [isAdmin, isVendedor]);
  
  // Add redemption dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedBenefitType, setSelectedBenefitType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Quick redeem dialog
  const [isQuickRedeemOpen, setIsQuickRedeemOpen] = useState(false);
  const [quickRedeemBenefit, setQuickRedeemBenefit] = useState('');

  // Edit PIN dialog
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [updatingPin, setUpdatingPin] = useState(false);

  // Toggle membership status dialog
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Renew membership dialog
  const [isRenewDialogOpen, setIsRenewDialogOpen] = useState(false);
  const [renewingMembership, setRenewingMembership] = useState(false);

  // Activation dialog
  const [isActivationDialogOpen, setIsActivationDialogOpen] = useState(false);

  // Discount purchase submission
  const [submittingPurchase, setSubmittingPurchase] = useState(false);

  // Delete member state
  const [deletingMember, setDeletingMember] = useState(false);

  // Admin profile
  const [adminName, setAdminName] = useState<string>('');
  
  // All system profiles for registrar name lookup (includes admins, sellers, and members)
  const [allSystemProfiles, setAllSystemProfiles] = useState<Record<string, { name: string; ubicacion: string | null }>>({});

  // Vendor's own purchases
  const [myPurchases, setMyPurchases] = useState<DiscountPurchase[]>([]);

  // Seller rank for header display
  const [sellerRank, setSellerRank] = useState<number | null>(null);
  
  // Pending training modules count for sellers
  const [pendingCoursesCount, setPendingCoursesCount] = useState(0);
  
  // Mobile ranking dialog state
  const [isRankingDialogOpen, setIsRankingDialogOpen] = useState(false);
  
  // Prize winner notification state
  const [showPrizeNotification, setShowPrizeNotification] = useState(false);
  
  // Use prizes hook for seller notifications
  const { 
    getClaimablePrize, 
    hasClaimablePrize, 
    formatMonthYear: formatPrizeMonthYear 
  } = usePrizes(user?.id);
  
  // Use seller achievements hook
  const {
    checkAndGrantAchievements,
    checkAndRevokeAchievements,
    newAchievement,
    dismissNewAchievement,
    refetch: refetchAchievements
  } = useSellerAchievements();
  
  // Fetch pending courses count for sellers
  useEffect(() => {
    if (isVendedor && !isAdmin && user) {
      const fetchPendingCourses = async () => {
        const { data, error } = await supabase
          .from('training_assignments')
          .select('id, status')
          .eq('seller_id', user.id)
          .in('status', ['pending', 'in_progress']);
        
        if (!error && data) {
          setPendingCoursesCount(data.length);
        }
      };
      fetchPendingCourses();
    }
  }, [isVendedor, isAdmin, user]);
  
  // Show ranking dialog on first login (stored in sessionStorage)
  useEffect(() => {
    if (isVendedor && !isAdmin && sellerRank !== null) {
      const hasSeenRanking = sessionStorage.getItem('hasSeenRankingDialog');
      if (!hasSeenRanking) {
        setIsRankingDialogOpen(true);
        sessionStorage.setItem('hasSeenRankingDialog', 'true');
      }
    }
  }, [isVendedor, isAdmin, sellerRank]);

  // Show prize winner notification if seller has a claimable prize
  useEffect(() => {
    if (isVendedor && !isAdmin && hasClaimablePrize()) {
      const hasSeenPrizeNotification = sessionStorage.getItem('hasSeenPrizeNotification');
      if (!hasSeenPrizeNotification) {
        // Delay slightly to not overlap with ranking dialog
        const timer = setTimeout(() => {
          setShowPrizeNotification(true);
          sessionStorage.setItem('hasSeenPrizeNotification', 'true');
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isVendedor, isAdmin, hasClaimablePrize]);

  // Filtered members based on search and filters
  const filteredMembers = useMemo(() => {
    const filtered = allMembers.filter(m => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (!m.profile.nombre.toLowerCase().includes(query) && 
            !m.profile.telefono.includes(query)) {
          return false;
        }
      }
      
      // Plan filter
      if (planFilter !== 'todos') {
        if (!m.membership || m.membership.plan !== planFilter) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'todos') {
        if (statusFilter === 'activo' && (!m.membership || !m.membership.is_active)) {
          return false;
        }
        if (statusFilter === 'pausado' && (!m.membership || m.membership.is_active)) {
          return false;
        }
        if (statusFilter === 'sin-plan' && m.membership) {
          return false;
        }
      }
      
      return true;
    });

    // Sort by registration date (newest first)
    return filtered.sort((a, b) => {
      return new Date(b.profile.created_at).getTime() - new Date(a.profile.created_at).getTime();
    });
  }, [allMembers, searchQuery, planFilter, statusFilter]);

  // Redirect if not admin or vendedor
  const hasAdminAccess = isAdmin || isVendedor;
  useEffect(() => {
    if (user && !hasAdminAccess) {
      navigate('/mi-membresia');
    }
  }, [user, hasAdminAccess, navigate]);

  // Auto-select first member on desktop when entering miembros tab
  useEffect(() => {
    // Solo auto-seleccionar para admins, no para vendedores
    if (isAdmin && !isVendedor && activeTab === 'miembros' && window.innerWidth >= 1024 && filteredMembers.length > 0 && !selectedMember) {
      setSelectedMember(filteredMembers[0]);
    }
  }, [isAdmin, isVendedor, activeTab, filteredMembers, selectedMember]);

  // Load admin profile and all members on mount
  useEffect(() => {
    if (hasAdminAccess && user) {
      fetchAllMembers();
      supabase
        .from('profiles')
        .select('nombre')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.nombre) {
            setAdminName(data.nombre.split(' ')[0]);
          }
        });
      
      // Fetch vendor's own purchases and ranking
      if (isVendedor && !isAdmin) {
        fetchMyPurchases();
        fetchSellerRank();
      }
    }
  }, [isAdmin, isVendedor, user]);

  const fetchSellerRank = async () => {
    if (!user) return;
    try {
      const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
      if (rankingData && Array.isArray(rankingData)) {
        const myRankIndex = rankingData.findIndex((s: any) => s.seller_id === user.id);
        if (myRankIndex >= 0) {
          setSellerRank(myRankIndex + 1);
        }
      }
    } catch (err) {
      console.error('Error fetching seller rank:', err);
    }
  };

  const fetchMyPurchases = async () => {
    if (!user) return;
    
    const currentMonth = getCurrentMonthYear();
    const { data } = await supabase
      .from('discount_purchases')
      .select('*')
      .eq('registered_by', user.id)
      .eq('month_year', currentMonth)
      .order('created_at', { ascending: false });
    
    if (data) {
      setMyPurchases(data as DiscountPurchase[]);
    }
  };

  // Fetch seller stats for achievement checking
  const fetchSellerStatsAndCheckAchievements = async () => {
    if (!user || !isVendedor) return;
    
    try {
      const currentMonth = getCurrentMonthYear();
      
      // Get seller's total and monthly sales
      const { data: allPurchases } = await supabase
        .from('discount_purchases')
        .select('id, savings, month_year')
        .eq('registered_by', user.id);
      
      const totalVentas = allPurchases?.length || 0;
      const ventasMes = allPurchases?.filter(p => p.month_year === currentMonth).length || 0;
      const totalAhorros = allPurchases?.reduce((sum, p) => sum + (p.savings || 0), 0) || 0;
      
      // Get seller's total redemptions
      const { data: allRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('id, quantity')
        .eq('redeemed_by', user.id);
      
      const totalCanjes = allRedemptions?.reduce((sum, r) => sum + (r.quantity || 1), 0) || 0;
      
      // Get training completion status
      const { data: trainingData } = await supabase
        .from('training_assignments')
        .select('status')
        .eq('seller_id', user.id);
      
      const trainingCompleted = trainingData && trainingData.length > 0 
        ? trainingData.every(t => t.status === 'completed')
        : false;
      
      // Get current ranking
      const currentRank = sellerRank || 99;
      
      // Check and grant achievements
      await checkAndGrantAchievements({
        totalVentas,
        ventasMes,
        totalCanjes,
        totalAhorros,
        trainingCompleted,
        ranking: currentRank
      });
      
    } catch (err) {
      console.error('Error checking achievements:', err);
    }
  };

  // Fetch seller stats and revoke achievements that no longer meet requirements
  const fetchSellerStatsAndRevokeAchievements = async () => {
    if (!user || !isVendedor) return;
    
    try {
      const currentMonth = getCurrentMonthYear();
      
      // Get seller's total and monthly sales
      const { data: allPurchases } = await supabase
        .from('discount_purchases')
        .select('id, savings, month_year')
        .eq('registered_by', user.id);
      
      const totalVentas = allPurchases?.length || 0;
      const ventasMes = allPurchases?.filter(p => p.month_year === currentMonth).length || 0;
      const totalAhorros = allPurchases?.reduce((sum, p) => sum + (p.savings || 0), 0) || 0;
      
      // Get seller's total redemptions
      const { data: allRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('id, quantity')
        .eq('redeemed_by', user.id);
      
      const totalCanjes = allRedemptions?.reduce((sum, r) => sum + (r.quantity || 1), 0) || 0;
      
      // Get training completion status
      const { data: trainingData } = await supabase
        .from('training_assignments')
        .select('status')
        .eq('seller_id', user.id);
      
      const trainingCompleted = trainingData && trainingData.length > 0 
        ? trainingData.every(t => t.status === 'completed')
        : false;
      
      // Get current ranking from API
      let currentRank = 99;
      try {
        const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
        if (rankingData && Array.isArray(rankingData)) {
          const myRankIndex = rankingData.findIndex((s: any) => s.seller_id === user.id);
          if (myRankIndex >= 0) {
            currentRank = myRankIndex + 1;
            setSellerRank(currentRank);
          }
        }
      } catch (rankErr) {
        console.error('Error getting ranking:', rankErr);
      }
      
      // Check and revoke achievements
      await checkAndRevokeAchievements({
        totalVentas,
        ventasMes,
        totalCanjes,
        totalAhorros,
        trainingCompleted,
        ranking: currentRank
      });
      
    } catch (err) {
      console.error('Error revoking achievements:', err);
    }
  };

  // Revoke achievements for ANY seller (used by admin when deleting sales)
  const revokeAchievementsForSeller = async (sellerId: string) => {
    if (!sellerId) return;
    
    try {
      const currentMonth = getCurrentMonthYear();
      
      // Get seller's total and monthly sales
      const { data: allPurchases } = await supabase
        .from('discount_purchases')
        .select('id, savings, month_year')
        .eq('registered_by', sellerId);
      
      const totalVentas = allPurchases?.length || 0;
      const ventasMes = allPurchases?.filter(p => p.month_year === currentMonth).length || 0;
      const totalAhorros = allPurchases?.reduce((sum, p) => sum + (p.savings || 0), 0) || 0;
      
      // Get seller's total redemptions
      const { data: allRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('id, quantity')
        .eq('redeemed_by', sellerId);
      
      const totalCanjes = allRedemptions?.reduce((sum, r) => sum + (r.quantity || 1), 0) || 0;
      
      // Get training completion status
      const { data: trainingData } = await supabase
        .from('training_assignments')
        .select('status')
        .eq('seller_id', sellerId);
      
      const trainingCompleted = trainingData && trainingData.length > 0 
        ? trainingData.every(t => t.status === 'completed')
        : false;
      
      // Get current ranking from API
      let currentRank = 99;
      try {
        const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
        if (rankingData && Array.isArray(rankingData)) {
          const rankIndex = rankingData.findIndex((s: any) => s.seller_id === sellerId);
          if (rankIndex >= 0) {
            currentRank = rankIndex + 1;
          }
        }
      } catch (rankErr) {
        console.error('Error getting ranking:', rankErr);
      }
      
      // Get seller's current achievements
      const { data: achievements } = await supabase
        .from('seller_achievements')
        .select('id, achievement_type')
        .eq('seller_id', sellerId);
      
      const toRevoke: string[] = [];
      
      for (const achievement of achievements || []) {
        const type = achievement.achievement_type;
        let shouldRevoke = false;
        
        switch (type) {
          case 'first_sale':
            shouldRevoke = totalVentas < 1;
            break;
          case 'sales_10':
            shouldRevoke = ventasMes < 10;
            break;
          case 'sales_50':
            shouldRevoke = totalVentas < 50;
            break;
          case 'sales_100':
            shouldRevoke = totalVentas < 100;
            break;
          case 'redemptions_50':
            shouldRevoke = totalCanjes < 50;
            break;
          case 'savings_100k':
            shouldRevoke = totalAhorros < 100000;
            break;
          case 'savings_500k':
            shouldRevoke = totalAhorros < 500000;
            break;
          case 'podium':
          case 'first_place':
            // Check if there's an official prize awarded to this seller
            // If no prize exists, the achievement was granted prematurely and should be revoked
            const { data: prizeData } = await supabase
              .from('monthly_prizes')
              .select('id')
              .eq('winner_id', sellerId)
              .in('status', ['claimable', 'claimed'])
              .limit(1);
            
            // Revoke if no official prize exists for this seller
            shouldRevoke = !prizeData || prizeData.length === 0;
            break;
          case 'training_complete':
            shouldRevoke = !trainingCompleted;
            break;
        }
        
        if (shouldRevoke) {
          toRevoke.push(achievement.id);
        }
      }
      
      // Delete revoked achievements
      for (const id of toRevoke) {
        await supabase
          .from('seller_achievements')
          .delete()
          .eq('id', id);
        console.log(`Achievement revoked for seller ${sellerId}: ${id}`);
      }
      
    } catch (err) {
      console.error('Error revoking achievements for seller:', err);
    }
  };

  const fetchAllMembers = async () => {
    setLoading(true);
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('nombre');

      if (profilesError) {
        if (import.meta.env.DEV) {
          console.error('Error fetching profiles:', profilesError);
        }
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los miembros',
          variant: 'destructive',
        });
        return;
      }

      const { data: systemRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['admin', 'vendedor']);

      const { data: memberships } = await supabase
        .from('memberships')
        .select('*');

      const systemUserIds = new Set((systemRoles || []).map(r => r.user_id));
      const membershipUserIds = new Set((memberships || []).map(m => m.user_id));
      
      // Only show profiles that: 1) are not admin/vendor AND 2) have a membership record
      const memberProfiles = (profiles || []).filter(p => 
        !systemUserIds.has(p.user_id) && membershipUserIds.has(p.user_id)
      );


      const { data: redemptions } = await supabase
        .from('benefit_redemptions')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: payments } = await supabase
        .from('membership_payments')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: discountPurchases } = await supabase
        .from('discount_purchases')
        .select('*')
        .order('created_at', { ascending: false });

      // Create a map of ALL user names and locations from profiles for redeemer/registrar lookup
      // (includes admins, sellers, and members)
      const profilesMap: Record<string, { name: string; ubicacion: string | null }> = {};
      (profiles || []).forEach(p => {
        profilesMap[p.user_id] = { 
          name: p.nombre.split(' ')[0], // First name only
          ubicacion: p.ubicacion_tienda || null 
        };
      });
      
      // Store the full profiles map in state for later use in updates
      setAllSystemProfiles(profilesMap);

      const membersWithData: MemberData[] = memberProfiles.map(profile => {
        const membership = (memberships || []).find(m => m.user_id === profile.user_id) || null;
        const memberRedemptions = membership 
          ? (redemptions || []).filter(r => r.membership_id === membership.id).map(r => ({
              ...r,
              redeemer_name: profilesMap[r.redeemed_by]?.name || 'Admin',
              redeemer_ubicacion: profilesMap[r.redeemed_by]?.ubicacion || null,
            }))
          : [];
        const memberPayments = membership
          ? (payments || []).filter(p => p.membership_id === membership.id).map(p => ({
              ...p,
              processor_name: profilesMap[p.processed_by]?.name || 'Admin'
            }))
          : [];
        const memberDiscountPurchases = membership
          ? (discountPurchases || []).filter(dp => dp.membership_id === membership.id).map(dp => ({
              ...dp,
              registrar_name: profilesMap[dp.registered_by]?.name || 'Admin',
              registrar_ubicacion: profilesMap[dp.registered_by]?.ubicacion || null,
            }))
          : [];

        return {
          profile,
          membership: membership as Membership | null,
          redemptions: memberRedemptions as Redemption[],
          payments: memberPayments as Payment[],
          discountPurchases: memberDiscountPurchases as DiscountPurchase[],
        };
      });

      setAllMembers(membersWithData);
      
      // Auto-select first member ONLY for admins (not sellers) on desktop
      if (isAdmin && !isVendedor && membersWithData.length > 0 && !selectedMember && window.innerWidth >= 1024) {
        setSelectedMember(membersWithData[0]);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error loading members:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRedeem = (benefitKey: string) => {
    // Block redemptions for inactive memberships
    if (!selectedMember?.membership?.is_active) {
      toast({
        title: 'Membresía no activa',
        description: 'No se pueden registrar canjes para membresías pendientes de activación',
        variant: 'destructive',
      });
      return;
    }
    setQuickRedeemBenefit(benefitKey);
    setIsQuickRedeemOpen(true);
  };

  const handleQuickRedeemConfirm = async (notes: string) => {
    if (!selectedMember?.membership || !quickRedeemBenefit) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('benefit_redemptions')
        .insert({
          membership_id: selectedMember.membership.id,
          benefit_type: quickRedeemBenefit,
          quantity: 1,
          month_year: getCurrentMonthYear(),
          notes: notes.trim() || null,
          redeemed_by: user?.id,
        });

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error adding redemption:', error);
        }
        
        // Check if it's a limit exceeded error from the database trigger
        const isLimitError = error.message?.includes('LIMIT_EXCEEDED');
        
        toast({
          title: isLimitError ? 'Límite alcanzado' : 'Error',
          description: isLimitError 
            ? 'Este beneficio ya fue canjeado. Actualiza la página para ver los cambios.'
            : 'No se pudo registrar el canje',
          variant: 'destructive',
        });
        
        // Refresh member data if limit was exceeded (another seller redeemed it)
        if (isLimitError && selectedMember?.membership) {
          const { data: updatedRedemptions } = await supabase
            .from('benefit_redemptions')
            .select('*')
            .eq('membership_id', selectedMember.membership.id)
            .order('created_at', { ascending: false });
          
          const updatedMember = {
            ...selectedMember,
            redemptions: updatedRedemptions || [],
          };
          
          setSelectedMember(updatedMember);
          setAllMembers(allMembers.map(m => 
            m.profile.user_id === selectedMember.profile.user_id
              ? updatedMember
              : m
          ));
        }
        
        setIsQuickRedeemOpen(false);
        return;
      }

      const benefitName = monthlyBenefits[selectedMember.membership.plan]?.[quickRedeemBenefit]?.name || quickRedeemBenefit;

      await logAdminAction('benefit_redemption', selectedMember.profile.user_id, {
        benefit_type: quickRedeemBenefit,
        quantity: 1,
        month_year: getCurrentMonthYear(),
        notes: notes.trim() || null,
      });

      toast({
        title: '¡Canje registrado!',
        description: `Se registró 1 ${benefitName}`,
      });

      const { data: updatedRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('*')
        .eq('membership_id', selectedMember.membership.id)
        .order('created_at', { ascending: false });

      const updatedMember = {
        ...selectedMember,
        redemptions: updatedRedemptions || [],
      };

      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m => 
        m.profile.user_id === selectedMember.profile.user_id
          ? updatedMember
          : m
      ));

      // Check for achievements after redemption
      if (isVendedor) {
        await fetchSellerStatsAndCheckAchievements();
      }

      setIsQuickRedeemOpen(false);
      setQuickRedeemBenefit('');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error with quick redeem:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRedemption = async () => {
    if (!selectedMember?.membership || !selectedBenefitType) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('benefit_redemptions')
        .insert({
          membership_id: selectedMember.membership.id,
          benefit_type: selectedBenefitType,
          quantity,
          month_year: getCurrentMonthYear(),
          notes: notes.trim() || null,
          redeemed_by: user?.id,
        });

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error adding redemption:', error);
        }
        
        // Check if it's a limit exceeded error from the database trigger
        const isLimitError = error.message?.includes('LIMIT_EXCEEDED');
        
        toast({
          title: isLimitError ? 'Límite alcanzado' : 'Error',
          description: isLimitError 
            ? 'Este beneficio ya fue canjeado. Actualiza la página para ver los cambios.'
            : 'No se pudo registrar el canje',
          variant: 'destructive',
        });
        
        // Refresh member data if limit was exceeded (another seller redeemed it)
        if (isLimitError && selectedMember?.membership) {
          const { data: updatedRedemptions } = await supabase
            .from('benefit_redemptions')
            .select('*')
            .eq('membership_id', selectedMember.membership.id)
            .order('created_at', { ascending: false });
          
          const updatedMember = {
            ...selectedMember,
            redemptions: updatedRedemptions || [],
          };
          
          setSelectedMember(updatedMember);
          setAllMembers(allMembers.map(m => 
            m.profile.user_id === selectedMember.profile.user_id
              ? updatedMember
              : m
          ));
        }
        
        setIsAddDialogOpen(false);
        return;
      }

      await logAdminAction('benefit_redemption', selectedMember.profile.user_id, {
        benefit_type: selectedBenefitType,
        quantity: quantity,
        month_year: getCurrentMonthYear(),
        notes: notes.trim() || null,
      });

      toast({
        title: '¡Canje registrado!',
        description: `Se registró ${quantity} ${benefitTypes.find(b => b.value === selectedBenefitType)?.label}`,
      });

      const { data: updatedRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('*')
        .eq('membership_id', selectedMember.membership.id)
        .order('created_at', { ascending: false });

      const updatedMember = {
        ...selectedMember,
        redemptions: updatedRedemptions || [],
      };

      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m => 
        m.profile.user_id === selectedMember.profile.user_id
          ? updatedMember
          : m
      ));

      // Check for achievements after redemption
      if (isVendedor) {
        await fetchSellerStatsAndCheckAchievements();
      }

      setIsAddDialogOpen(false);
      setSelectedBenefitType('');
      setQuantity(1);
      setNotes('');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error adding redemption:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const logAdminAction = async (
    actionType: string,
    targetUserId: string,
    details?: Record<string, unknown>
  ) => {
    try {
      await supabase
        .from('admin_audit_logs' as 'profiles')
        .insert({
          admin_user_id: user?.id,
          action_type: actionType,
          target_user_id: targetUserId,
          details: details || null,
        } as never);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error logging admin action:', err);
      }
    }
  };

  const generateRandomPin = () => {
    const pin = Math.floor(100000 + Math.random() * 900000).toString();
    setNewPin(pin);
  };

  const handleUpdatePin = async () => {
    if (!selectedMember || !/^\d{6}$/.test(newPin)) {
      toast({
        title: 'Error',
        description: 'El PIN debe ser exactamente 6 dígitos',
        variant: 'destructive',
      });
      return;
    }

    setUpdatingPin(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ pin: newPin })
        .eq('user_id', selectedMember.profile.user_id);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error updating PIN:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo actualizar el PIN',
          variant: 'destructive',
        });
        return;
      }

      await logAdminAction('pin_change', selectedMember.profile.user_id, {
        changed_at: new Date().toISOString(),
      });

      toast({
        title: '¡PIN actualizado!',
        description: `El nuevo PIN es: ${newPin}`,
      });

      const updatedMember = {
        ...selectedMember,
        profile: { ...selectedMember.profile, pin: newPin },
      };
      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m =>
        m.profile.user_id === selectedMember.profile.user_id ? updatedMember : m
      ));

      setIsPinDialogOpen(false);
      setNewPin('');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error updating PIN:', err);
      }
    } finally {
      setUpdatingPin(false);
    }
  };

  const handleToggleMembershipStatus = async () => {
    if (!selectedMember?.membership) return;

    setUpdatingStatus(true);
    try {
      const membership = selectedMember.membership;
      const isCurrentlyActive = membership.is_active;
      
      let updateData: { is_active: boolean; end_date?: string; start_date?: string };
      
      if (isCurrentlyActive) {
        // Cancelar: solo desactivar
        updateData = { is_active: false };
      } else {
        // Reactivar: iniciar NUEVO ciclo completo basado en billing_period
        const today = new Date();
        const billingPeriodMonths: Record<string, number> = {
          mensual: 1,
          trimestral: 3,
          anual: 12
        };
        const months = billingPeriodMonths[membership.billing_period] || 1;
        const newEndDate = new Date(today);
        newEndDate.setMonth(newEndDate.getMonth() + months);
        
        updateData = { 
          is_active: true, 
          start_date: today.toISOString().split('T')[0],
          end_date: newEndDate.toISOString().split('T')[0]
        };
      }

      const { error } = await supabase
        .from('memberships')
        .update(updateData)
        .eq('id', membership.id);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error updating membership status:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo actualizar el estado de la membresía',
          variant: 'destructive',
        });
        return;
      }

      // Si se reactiva, registrar el pago
      if (!isCurrentlyActive) {
        const { PLAN_PRICING } = await import('@/components/membership/types');
        const amount = PLAN_PRICING[membership.plan][membership.billing_period];
        
        const { error: paymentError } = await supabase
          .from('membership_payments')
          .insert({
            membership_id: membership.id,
            user_id: selectedMember.profile.user_id,
            amount: amount,
            plan: membership.plan,
            billing_period: membership.billing_period,
            payment_type: 'renewal',
            processed_by: user?.id,
            notes: 'Reactivación de membresía',
          });

        if (paymentError) {
          console.error('Error recording payment:', paymentError);
        }
      }

      await logAdminAction(
        isCurrentlyActive ? 'membership_deactivate' : 'membership_activate',
        selectedMember.profile.user_id,
        {
          previous_status: isCurrentlyActive,
          new_status: !isCurrentlyActive,
          ...(isCurrentlyActive ? {} : { 
            payment_recorded: true,
            billing_period: membership.billing_period 
          }),
        }
      );

      toast({
        title: isCurrentlyActive ? '¡Membresía cancelada!' : '¡Membresía reactivada!',
        description: isCurrentlyActive 
          ? 'La membresía ha sido cancelada' 
          : 'Se inició un nuevo ciclo y se registró el pago',
      });

      await fetchAllMembers();
      
      const { data: updatedMembership } = await supabase
        .from('memberships')
        .select('*')
        .eq('id', membership.id)
        .maybeSingle();
      
      if (updatedMembership) {
        const updatedMember = {
          ...selectedMember,
          membership: updatedMembership as Membership,
        };
        setSelectedMember(updatedMember);
      }

      setIsStatusDialogOpen(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error toggling membership status:', err);
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleRenewMembership = async (billingPeriod: 'mensual' | 'trimestral' | 'anual') => {
    if (!selectedMember?.membership) return;

    setRenewingMembership(true);
    try {
      const today = new Date();
      let newEndDate = new Date(today);
      
      switch (billingPeriod) {
        case 'mensual':
          newEndDate.setMonth(newEndDate.getMonth() + 1);
          break;
        case 'trimestral':
          newEndDate.setMonth(newEndDate.getMonth() + 3);
          break;
        case 'anual':
          newEndDate.setFullYear(newEndDate.getFullYear() + 1);
          break;
      }

      const { error } = await supabase
        .from('memberships')
        .update({
          billing_period: billingPeriod,
          start_date: today.toISOString().split('T')[0],
          end_date: newEndDate.toISOString().split('T')[0],
          is_active: true,
        })
        .eq('id', selectedMember.membership.id);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error renewing membership:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo renovar la membresía',
          variant: 'destructive',
        });
        return;
      }

      await logAdminAction('membership_renewal', selectedMember.profile.user_id, {
        previous_end_date: selectedMember.membership.end_date,
        new_end_date: newEndDate.toISOString().split('T')[0],
        billing_period: billingPeriod,
      });

      toast({
        title: '¡Membresía renovada!',
        description: `Nueva fecha de vencimiento: ${newEndDate.toLocaleDateString('es-CR')}`,
      });

      await fetchAllMembers();
      
      const { data: updatedMembership } = await supabase
        .from('memberships')
        .select('*')
        .eq('id', selectedMember.membership.id)
        .maybeSingle();
      
      if (updatedMembership) {
        const updatedMember = {
          ...selectedMember,
          membership: updatedMembership as Membership,
        };
        setSelectedMember(updatedMember);
      }

      setIsRenewDialogOpen(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error renewing membership:', err);
      }
    } finally {
    setRenewingMembership(false);
    }
  };

  const handleContactMember = (member: MemberData, message: string) => {
    const digits = member.profile.telefono.replace(/\D/g, '');
    // Prepend 506 if number is 8 digits (Costa Rica)
    const phone = digits.length === 8 ? '506' + digits : digits;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Delete member completely (admin only)
  const handleDeleteMember = async () => {
    if (!selectedMember || !isAdmin) return;

    setDeletingMember(true);
    try {
      const userId = selectedMember.profile.user_id;
      const membershipId = selectedMember.membership?.id;

      // Delete in order: discount_purchases, benefit_redemptions, membership_payments, memberships, audit_logs, user_roles, profiles
      // Each delete checks for errors and throws if failed
      
      if (membershipId) {
        const { error: dpError } = await supabase.from('discount_purchases').delete().eq('membership_id', membershipId);
        if (dpError) throw dpError;
        
        const { error: brError } = await supabase.from('benefit_redemptions').delete().eq('membership_id', membershipId);
        if (brError) throw brError;
        
        const { error: mpError } = await supabase.from('membership_payments').delete().eq('membership_id', membershipId);
        if (mpError) throw mpError;
        
        const { error: memError } = await supabase.from('memberships').delete().eq('id', membershipId);
        if (memError) throw memError;
      }

      const { error: auditError } = await supabase.from('admin_audit_logs').delete().eq('target_user_id', userId);
      if (auditError) throw auditError;

      const { error: roleError } = await supabase.from('user_roles').delete().eq('user_id', userId);
      if (roleError) throw roleError;

      const { error: profileError } = await supabase.from('profiles').delete().eq('user_id', userId);
      if (profileError) throw profileError;

      toast({
        title: '¡Miembro eliminado!',
        description: `${selectedMember.profile.nombre} ha sido eliminado permanentemente.`,
      });

      // Refresh from backend to ensure sync
      setSelectedMember(null);
      await fetchAllMembers();
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error deleting member:', err);
      }
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el miembro. Verifique los permisos.',
        variant: 'destructive',
      });
      // Refresh to sync state with backend
      await fetchAllMembers();
    } finally {
      setDeletingMember(false);
    }
  };

  const handleRegisterDiscountPurchase = async (data: {
    product_name: string;
    category: string;
    original_price: number;
    discount_percentage: number;
    final_price: number;
    savings: number;
    quantity: number;
    notes: string;
  }) => {
    if (!selectedMember?.membership || !user) return;

    // Block purchases for inactive memberships
    if (!selectedMember.membership.is_active) {
      toast({
        title: 'Membresía no activa',
        description: 'No se pueden registrar compras para membresías pendientes de activación',
        variant: 'destructive',
      });
      return;
    }

    setSubmittingPurchase(true);
    try {
      const now = new Date();
      const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      const { error } = await supabase
        .from('discount_purchases')
        .insert({
          membership_id: selectedMember.membership.id,
          user_id: selectedMember.profile.user_id,
          product_name: data.product_name,
          category: data.category,
          original_price: data.original_price,
          discount_percentage: data.discount_percentage,
          final_price: data.final_price,
          savings: data.savings,
          quantity: data.quantity,
          notes: data.notes.trim() || null,
          registered_by: user.id,
          month_year: monthYear,
        });

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error registering purchase:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo registrar la compra',
          variant: 'destructive',
        });
        return;
      }

      await logAdminAction('discount_purchase', selectedMember.profile.user_id, {
        product_name: data.product_name,
        category: data.category,
        original_price: data.original_price,
        final_price: data.final_price,
        savings: data.savings,
      });

      toast({
        title: '¡Compra registrada!',
        description: `${data.product_name} - Ahorro: ₡${data.savings.toLocaleString()}`,
      });

      // Refresh member data
      const { data: updatedPurchases } = await supabase
        .from('discount_purchases')
        .select('*')
        .eq('membership_id', selectedMember.membership.id)
        .order('created_at', { ascending: false });

      const updatedMember = {
        ...selectedMember,
        discountPurchases: (updatedPurchases || []).map(dp => ({
          ...dp,
          registrar_name: allSystemProfiles[dp.registered_by]?.name || 'Admin',
          registrar_ubicacion: allSystemProfiles[dp.registered_by]?.ubicacion || null,
        })) as DiscountPurchase[],
      };

      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m =>
        m.profile.user_id === selectedMember.profile.user_id ? updatedMember : m
      ));

      // Refresh vendor's own purchases list and check achievements
      if (isVendedor) {
        if (!isAdmin) {
          fetchMyPurchases();
        }
        // Check for new achievements after sale
        await fetchSellerStatsAndCheckAchievements();
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error with discount purchase:', err);
      }
    } finally {
      setSubmittingPurchase(false);
    }
  };

  // Delete redemption handler
  const handleDeleteRedemption = async (redemptionId: string, reason: string) => {
    if (!selectedMember?.membership) return;

    try {
      // Get redemption data before deleting
      const { data: redemptionData } = await supabase
        .from('benefit_redemptions')
        .select('*')
        .eq('id', redemptionId)
        .maybeSingle();

      if (!redemptionData) {
        toast({
          title: 'Error',
          description: 'No se encontró el canje',
          variant: 'destructive',
        });
        return;
      }

      // Delete the redemption
      const { error } = await supabase
        .from('benefit_redemptions')
        .delete()
        .eq('id', redemptionId);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error deleting redemption:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo eliminar el canje',
          variant: 'destructive',
        });
        return;
      }

      // Log the deletion
      await logAdminAction('redemption_deleted', selectedMember.profile.user_id, {
        benefit_type: redemptionData.benefit_type,
        quantity: redemptionData.quantity,
        original_date: redemptionData.created_at,
        reason: reason,
      });

      toast({
        title: 'Canje eliminado',
        description: isVendedor ? 'Tu ranking ha sido actualizado' : 'El registro quedó en el historial de auditoría',
      });

      // Refresh member data
      const { data: updatedRedemptions } = await supabase
        .from('benefit_redemptions')
        .select('*')
        .eq('membership_id', selectedMember.membership.id)
        .order('created_at', { ascending: false });

      const updatedMember = {
        ...selectedMember,
        redemptions: updatedRedemptions || [],
      };

      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m =>
        m.profile.user_id === selectedMember.profile.user_id ? updatedMember : m
      ));

      // Check and revoke achievements for the affected seller
      if (redemptionData.redeemed_by) {
        if (isVendedor && !isAdmin && redemptionData.redeemed_by === user?.id) {
          await fetchSellerStatsAndRevokeAchievements();
        } else if (isAdmin) {
          await revokeAchievementsForSeller(redemptionData.redeemed_by);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error deleting redemption:', err);
      }
      toast({
        title: 'Error',
        description: 'Ocurrió un error al eliminar el canje',
        variant: 'destructive',
      });
    }
  };

  // Delete purchase handler
  const handleDeletePurchase = async (purchaseId: string, reason: string) => {
    if (!selectedMember?.membership) return;

    try {
      // Get purchase data before deleting
      const { data: purchaseData } = await supabase
        .from('discount_purchases')
        .select('*')
        .eq('id', purchaseId)
        .maybeSingle();

      if (!purchaseData) {
        toast({
          title: 'Error',
          description: 'No se encontró la compra',
          variant: 'destructive',
        });
        return;
      }

      // Delete the purchase
      const { error } = await supabase
        .from('discount_purchases')
        .delete()
        .eq('id', purchaseId);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error deleting purchase:', error);
        }
        toast({
          title: 'Error',
          description: 'No se pudo eliminar la compra',
          variant: 'destructive',
        });
        return;
      }

      // Log the deletion
      await logAdminAction('purchase_deleted', selectedMember.profile.user_id, {
        product_name: purchaseData.product_name,
        category: purchaseData.category,
        savings: purchaseData.savings,
        original_date: purchaseData.created_at,
        reason: reason,
      });

      toast({
        title: 'Compra eliminada',
        description: isVendedor ? 'Tu ranking ha sido actualizado' : 'El registro quedó en el historial de auditoría',
      });

      // Refresh member data
      const { data: updatedPurchases } = await supabase
        .from('discount_purchases')
        .select('*')
        .eq('membership_id', selectedMember.membership.id)
        .order('created_at', { ascending: false });

      const updatedMember = {
        ...selectedMember,
        discountPurchases: (updatedPurchases || []).map(dp => ({
          ...dp,
          registrar_name: allSystemProfiles[dp.registered_by]?.name || 'Admin',
          registrar_ubicacion: allSystemProfiles[dp.registered_by]?.ubicacion || null,
        })) as DiscountPurchase[],
      };

      setSelectedMember(updatedMember);
      setAllMembers(allMembers.map(m =>
        m.profile.user_id === selectedMember.profile.user_id ? updatedMember : m
      ));

      // Refresh vendor's own purchases list and check achievement revocation
      if (isVendedor && !isAdmin) {
        fetchMyPurchases();
        // Recalculate stats and revoke achievements if needed
        await fetchSellerStatsAndRevokeAchievements();
      } else if (isAdmin && purchaseData.registered_by) {
        // Admin deleting a seller's sale - revoke achievements for that seller
        await revokeAchievementsForSeller(purchaseData.registered_by);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error deleting purchase:', err);
      }
      toast({
        title: 'Error',
        description: 'Ocurrió un error al eliminar la compra',
        variant: 'destructive',
      });
    }
  };

  // Count total alerts for badge (activations + unused benefits + payment due)
  const pendingActivationsCount = allMembers.filter(m => m.membership && !m.membership.is_active && !m.membership.activated_at).length;
  
  const membersWithUnusedCount = allMembers
    .filter(m => m.membership && m.membership.is_active)
    .filter(member => {
      if (!member.membership) return false;
      const benefits = monthlyBenefits[member.membership.plan];
      return Object.entries(benefits).some(([key, benefit]) => {
        const used = getUsedBenefitsThisMonth(member.redemptions, key);
        return used < benefit.max;
      });
    }).length;
  
  const membersWithPaymentDueCount = isAdmin ? allMembers
    .filter(m => m.membership && m.membership.is_active)
    .filter(m => {
      const status = isNearExpiration(m.membership);
      return status.isExpiring;
    }).length : 0;
  
  const pendingActionsCount = pendingActivationsCount + membersWithUnusedCount + membersWithPaymentDueCount;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        {/* Background glow effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        </div>
        <div className="flex flex-col items-center gap-4 relative">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-white/70">Cargando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 px-3 py-4 sm:p-4 pb-24">
      {/* Background glow effects - same as login */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      </div>
      
      <AnimatedBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header - Mobile: logo center, logout right. Desktop: logo left, stats center, logout right */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between gap-4 mb-4 pt-4 pb-4 px-4 -mx-4 bg-white/5 backdrop-blur-xl border-b border-white/10 rounded-t-2xl"
        >
          {/* Desktop: Logo left */}
          <img src={villatechBlueLogo} alt="VillaTech" className="hidden lg:block h-10 flex-shrink-0" />
          
          {/* Mobile: Trophy icon left (for vendors) - opens ranking dialog */}
          {isVendedor && !isAdmin && sellerRank !== null ? (
            <button 
              onClick={() => setIsRankingDialogOpen(true)}
              className="lg:hidden flex items-center hover:scale-105 transition-transform"
            >
              <div className="relative w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center border border-slate-500/50">
                <Trophy className="w-4 h-4 text-slate-300" />
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-black flex items-center justify-center text-[10px] font-bold text-slate-300 border border-slate-500/50">
                  {sellerRank}
                </span>
              </div>
            </button>
          ) : (
            <div className="lg:hidden w-6" />
          )}
          
          {/* Mobile Ranking Dialog */}
          <Dialog open={isRankingDialogOpen} onOpenChange={setIsRankingDialogOpen}>
            <DialogContent className="bg-slate-900 border-slate-700 max-w-[340px] p-0 gap-0">
              <DialogHeader className="sr-only">
                <DialogTitle>Tu Ranking</DialogTitle>
              </DialogHeader>
              <div className="p-4">
                <SellerRankingCard onRankUpdate={(rank) => setSellerRank(rank)} />
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Confetti celebration for #1 rank */}
          <Confetti isActive={isRankingDialogOpen && sellerRank === 1} />

          {/* Mobile: Logo center */}
          <img src={villatechBlueLogo} alt="VillaTech" className="lg:hidden h-10 flex-shrink-0" />
          
          {/* Spacer for desktop layout */}
          <div className="hidden lg:flex flex-1" />

          {/* Logout right */}
          <button 
            onClick={async () => {
              await supabase.auth.signOut();
              navigate('/login');
            }}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors group flex-shrink-0"
          >
            <LogOut className="w-5 h-5 sm:w-4 sm:h-4 group-hover:scale-110 transition-transform" />
            <span className="hidden lg:inline">Salir</span>
          </button>
        </motion.div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">

          {/* Tab: Miembros */}
          <TabsContent value="miembros" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
              {/* Members sidebar - hidden on mobile when member is selected */}
              <div className={`flex flex-col gap-2 ${selectedMember ? 'hidden lg:flex' : 'flex'}`}>
                {/* Welcome - centered on mobile, above card on desktop */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center lg:text-left"
                >
                  <h1 className="text-xl sm:text-2xl font-bold text-white">
                    {adminName ? `¡Hola, ${adminName}!` : 'Panel de Administración'}
                  </h1>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 lg:sticky lg:top-4 flex flex-col"
                >
                <MemberFilters
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  planFilter={planFilter}
                  onPlanFilterChange={setPlanFilter}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                />

                <div className="mt-4">
                  <h2 className="text-sm font-medium text-white/50 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Clientes ({filteredMembers.length})
                  </h2>
                  
                  {filteredMembers.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-8">
                      No se encontraron clientes
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto flex-1">
                      {filteredMembers.map((member) => {
                        const isPending = !member.membership || !member.membership.is_active;

                        return (
                          <button
                            key={member.profile.user_id}
                            onClick={() => setSelectedMember(
                              selectedMember?.profile.user_id === member.profile.user_id ? null : member
                            )}
                            className={`w-full p-3 rounded-xl text-left transition-all ${
                              isPending
                                ? selectedMember?.profile.user_id === member.profile.user_id
                                  ? 'bg-red-500/30 border border-red-400/50'
                                  : 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20'
                                : selectedMember?.profile.user_id === member.profile.user_id
                                  ? 'bg-blue-500/20 border border-blue-500/30'
                                  : 'bg-white/5 hover:bg-white/10 border border-transparent'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  {isPending && (
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                                  )}
                                  <p className="text-white font-medium truncate">{member.profile.nombre}</p>
                                </div>
                                <a 
                                  href={`https://wa.me/${(() => {
                                    const digits = member.profile.telefono.replace(/\D/g, '');
                                    return digits.length === 8 ? '506' + digits : digits;
                                  })()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-green-400/70 hover:text-green-400 text-xs hover:underline"
                                >
                                  📱 {member.profile.telefono}
                                </a>
                                <p className="text-white/40 text-xs mt-0.5">
                                  📅 {formatDate(member.profile.created_at)}
                                </p>
                              </div>
                              {member.membership && member.membership.is_active ? (
                                <span className={`text-xs px-2 py-1 rounded-full capitalize flex-shrink-0 ml-2 ${
                                  member.membership.plan === 'oro' 
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : member.membership.plan === 'plata'
                                    ? 'bg-gray-500/20 text-gray-300'
                                    : 'bg-amber-700/20 text-amber-400'
                                }`}>
                                  {member.membership.plan}
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 flex-shrink-0 ml-2">
                                  {member.membership ? 'Pausado' : 'Sin plan'}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
              </div>

              {/* Member details panel - mobile (after member list) */}
              {selectedMember && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="lg:hidden"
                >
                  <button 
                    onClick={() => setSelectedMember(null)}
                    className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Volver a la lista
                  </button>
                  <MemberDetailsPanel
                    member={selectedMember}
                    onEditPin={() => {
                      setNewPin(selectedMember?.profile.pin || '');
                      setIsPinDialogOpen(true);
                    }}
                    onStatusToggle={() => setIsStatusDialogOpen(true)}
                    onRenew={() => setIsRenewDialogOpen(true)}
                    onQuickRedeem={handleQuickRedeem}
                    onActivate={() => setIsActivationDialogOpen(true)}
                    onDelete={handleDeleteMember}
                    onRegisterPurchase={handleRegisterDiscountPurchase}
                    onDeleteRedemption={handleDeleteRedemption}
                    onDeletePurchase={handleDeletePurchase}
                    currentUserId={user?.id}
                    isAdminUser={isAdmin}
                    isSubmittingPurchase={submittingPurchase}
                    isDeleting={deletingMember}
                    canActivate={isAdmin}
                    canToggleStatus={isAdmin}
                    canDelete={isAdmin}
                  />
                </motion.div>
              )}

              {/* Member details panel - desktop only (right column) */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="hidden lg:block"
              >
                <MemberDetailsPanel
                  member={selectedMember}
                  onEditPin={() => {
                    setNewPin(selectedMember?.profile.pin || '');
                    setIsPinDialogOpen(true);
                  }}
                  onStatusToggle={() => setIsStatusDialogOpen(true)}
                  onRenew={() => setIsRenewDialogOpen(true)}
                  onQuickRedeem={handleQuickRedeem}
                  onActivate={() => setIsActivationDialogOpen(true)}
                  onDelete={handleDeleteMember}
                  onRegisterPurchase={handleRegisterDiscountPurchase}
                  onDeleteRedemption={handleDeleteRedemption}
                  onDeletePurchase={handleDeletePurchase}
                  currentUserId={user?.id}
                  isAdminUser={isAdmin}
                  isSubmittingPurchase={submittingPurchase}
                  isDeleting={deletingMember}
                  canActivate={isAdmin}
                  canToggleStatus={isAdmin}
                  canDelete={isAdmin}
                />
              </motion.div>

              {/* Vendor's own purchases list + Discount Card - only visible to vendors on desktop */}
              {isVendedor && !isAdmin && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hidden lg:block lg:col-span-2 mt-6 space-y-6"
                >
                  <DiscountRemindersCard plan="oro" />
                  <MyDiscountPurchasesList purchases={myPurchases} />
                </motion.div>
              )}
            </div>

            {/* Vendor's own purchases list + Discount Card - mobile (after member details) */}
            {isVendedor && !isAdmin && !selectedMember && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="lg:hidden mt-6 space-y-6"
              >
                <DiscountRemindersCard plan="oro" />
                <MyDiscountPurchasesList purchases={myPurchases} />
              </motion.div>
            )}
          </TabsContent>

          {/* Tab: Acciones */}
          <TabsContent value="acciones" className="mt-0">
            <ActionsPanel
              members={allMembers}
              onSelectMember={setSelectedMember}
              onContactMember={handleContactMember}
              onGoToMembers={() => setActiveTab('miembros')}
              isAdmin={isAdmin}
            />
          </TabsContent>

          {/* Tab: Equipo/Ranking */}
          <TabsContent value="estadisticas" className="mt-0">
            {/* Vendedores ven ranking con subtabs */}
            {isVendedor && !isAdmin ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Sub-tabs para vendedores */}
                <div className="flex gap-2 mb-6 overflow-x-auto">
                  <button
                    onClick={() => setRankingSubTab('posiciones')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      rankingSubTab === 'posiciones'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <Trophy className="w-4 h-4" />
                    Ranking
                  </button>
                  <button
                    onClick={() => setRankingSubTab('logros')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      rankingSubTab === 'logros'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <Medal className="w-4 h-4" />
                    Mis Logros
                  </button>
                  <button
                    onClick={() => setRankingSubTab('premios')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      rankingSubTab === 'premios'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <Gift className="w-4 h-4" />
                    Premios
                  </button>
                  <button
                    onClick={() => setRankingSubTab('puntos')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      rankingSubTab === 'puntos'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    Puntos
                  </button>
                  <button
                    onClick={() => setRankingSubTab('reglas')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      rankingSubTab === 'reglas'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Reglas
                  </button>
                </div>

                {/* Contenido según subtab */}
                {rankingSubTab === 'posiciones' && <SellerRankingDashboard />}
                {rankingSubTab === 'logros' && <AchievementsScreen />}
                {rankingSubTab === 'premios' && <PrizesScreen />}
                {rankingSubTab === 'puntos' && <ScoreSummaryTab />}
                {rankingSubTab === 'reglas' && <GameRulesEmbedded isAdmin={false} />}
              </motion.div>
            ) : (
              <>
                {/* Sub-tabs principales - Solo admins (3 tabs) */}
                <div className="flex gap-2 mb-6 overflow-x-auto">
                  <button
                    onClick={() => setEquipoSubTab('ranking')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      equipoSubTab === 'ranking'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <Trophy className="w-4 h-4" />
                    Ranking
                  </button>
                  <button
                    onClick={() => setEquipoSubTab('cursos')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      equipoSubTab === 'cursos'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <GraduationCap className="w-4 h-4" />
                    Cursos
                  </button>
                  <button
                    onClick={() => setEquipoSubTab('crear')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                      equipoSubTab === 'crear'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Crear
                  </button>
                </div>

                {/* Contenido según sub-tab seleccionado */}
                {equipoSubTab === 'ranking' && (
                  <div className="space-y-4">
                    {/* Sub-tabs dentro de Ranking */}
                    <div className="grid grid-cols-5 gap-2">
                      <button
                        onClick={() => setRankingSubTab('posiciones')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          rankingSubTab === 'posiciones'
                            ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <Trophy className="w-4 h-4" />
                        <span className="text-sm font-medium">Posiciones</span>
                      </button>
                      <button
                        onClick={() => setRankingSubTab('puntos')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          rankingSubTab === 'puntos'
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span className="text-sm font-medium">Puntos</span>
                      </button>
                      <button
                        onClick={() => setRankingSubTab('premios')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          rankingSubTab === 'premios'
                            ? 'bg-green-500/20 border-green-500/50 text-green-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <Gift className="w-4 h-4" />
                        <span className="text-sm font-medium">Premios</span>
                      </button>
                      <button
                        onClick={() => setRankingSubTab('logros')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          rankingSubTab === 'logros'
                            ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <Award className="w-4 h-4" />
                        <span className="text-sm font-medium">Logros</span>
                      </button>
                      <button
                        onClick={() => setRankingSubTab('reglas')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          rankingSubTab === 'reglas'
                            ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        <span className="text-sm font-medium">Reglas</span>
                      </button>
                    </div>

                    {/* Contenido de Ranking según sub-tab */}
                    {rankingSubTab === 'posiciones' && <SellerRankingDashboard />}
                    {rankingSubTab === 'puntos' && <ScoreSummaryTab />}
                    {rankingSubTab === 'premios' && <AdminPrizeManager adminId={user?.id || ''} />}
                    {rankingSubTab === 'logros' && <AchievementsScreen />}
                    {rankingSubTab === 'reglas' && <GameRulesEmbedded isAdmin={true} />}
                  </div>
                )}
                {equipoSubTab === 'cursos' && <TrainingSection isAdmin={isAdmin} />}
                {equipoSubTab === 'crear' && (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Equipo VillaTech</h3>
                        <p className="text-sm text-white/60">Gestiona y crea miembros del equipo</p>
                      </div>
                    </div>

                    {/* Sub-tabs dentro de Crear */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setCrearSubTab('equipo')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          crearSubTab === 'equipo'
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        <span className="text-sm font-medium">Equipo</span>
                      </button>
                      <button
                        onClick={() => setCrearSubTab('nuevo')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                          crearSubTab === 'nuevo'
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="text-sm font-medium">Nuevo</span>
                      </button>
                    </div>

                    {/* Contenido de Crear según sub-tab */}
                    {crearSubTab === 'equipo' && <TeamManagementSection />}
                    {crearSubTab === 'nuevo' && <CreateTeamMemberForm />}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Tab: Cursos - Solo visible para vendedores */}
          {isVendedor && !isAdmin && (
            <TabsContent value="cursos" className="mt-0">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <SellerTrainingView />
              </motion.div>
            </TabsContent>
          )}

          {/* Tab: Actividad */}
          <TabsContent value="actividad" className="mt-0">
            <ActivityTimeline 
              isAdmin={isAdmin} 
              currentUserId={user?.id} 
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Redemption Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Agregar Canje para {selectedMember?.profile.nombre}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Tipo de beneficio</Label>
              <Select value={selectedBenefitType} onValueChange={setSelectedBenefitType}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                  <SelectValue placeholder="Selecciona un beneficio" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/20">
                  {benefitTypes.map((benefit) => (
                    <SelectItem 
                      key={benefit.value} 
                      value={benefit.value}
                      className="text-white hover:bg-white/10"
                    >
                      {benefit.icon} {benefit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/70">Cantidad</Label>
              <Select value={quantity.toString()} onValueChange={(v) => setQuantity(parseInt(v))}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/20">
                  {[1, 2, 3].map((n) => (
                    <SelectItem 
                      key={n} 
                      value={n.toString()}
                      className="text-white hover:bg-white/10"
                    >
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/70">Notas (opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: Vidrio instalado en tienda"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddRedemption}
              disabled={!selectedBenefitType || submitting}
              className="bg-gradient-to-r from-blue-500 to-blue-600"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Registrar Canje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PIN Dialog */}
      <Dialog open={isPinDialogOpen} onOpenChange={setIsPinDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Editar PIN de {selectedMember?.profile.nombre?.split(' ')[0]}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <PinDisplay value={newPin} variant="blue" />
            
            <PinKeypad
              onNumberClick={(num) => newPin.length < 6 && setNewPin(newPin + num)}
              onDelete={() => setNewPin(newPin.slice(0, -1))}
              variant="blue"
            />

            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={generateRandomPin}
                className="border-white/20 text-white hover:bg-white/10 gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Generar PIN aleatorio
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsPinDialogOpen(false);
                setNewPin('');
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdatePin}
              disabled={newPin.length !== 6 || updatingPin}
              className="bg-gradient-to-r from-blue-500 to-blue-600"
            >
              {updatingPin ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Membership Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {selectedMember?.membership?.is_active ? 'Cancelar Membresía' : 'Reactivar Membresía'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedMember?.membership?.is_active ? (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">¿Cancelar la membresía de {selectedMember?.profile.nombre}?</p>
                    <p className="text-white/60 text-sm mt-1">
                      La membresía se cancelará y el usuario perderá acceso a todos los beneficios. Para reactivar, deberá pagar un nuevo ciclo completo.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Play className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">¿Reactivar la membresía de {selectedMember?.profile.nombre}?</p>
                    <p className="text-white/60 text-sm mt-1">
                      Se iniciará un NUEVO ciclo de facturación desde hoy. La fecha de vencimiento se calculará según el período de facturación.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedMember?.membership && (
              <div className="bg-white/5 rounded-xl p-3 text-sm">
                <div className="flex justify-between text-white/60">
                  <span>Plan actual:</span>
                  <span className="text-white capitalize">{selectedMember.membership.plan}</span>
                </div>
                <div className="flex justify-between text-white/60 mt-1">
                  <span>Período:</span>
                  <span className="text-white capitalize">{selectedMember.membership.billing_period}</span>
                </div>
                {!selectedMember.membership.is_active && (
                  <>
                    <div className="flex justify-between text-white/60 mt-1">
                      <span>Nueva fecha de vencimiento:</span>
                      <span className="text-green-400">
                        {(() => {
                          const today = new Date();
                          const months: Record<string, number> = { mensual: 1, trimestral: 3, anual: 12 };
                          today.setMonth(today.getMonth() + (months[selectedMember.membership.billing_period] || 1));
                          return today.toLocaleDateString('es-CR');
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-400 mt-3 pt-3 border-t border-white/10 font-semibold">
                      <span>Monto a cobrar:</span>
                      <span>
                        {(() => {
                          const pricing: Record<string, Record<string, number>> = {
                            bronce: { mensual: 12000, anual: 120000 },
                            plata: { mensual: 27000, anual: 270000 },
                            oro: { mensual: 45000, anual: 450000 },
                          };
                          const amount = pricing[selectedMember.membership.plan]?.[selectedMember.membership.billing_period] || 0;
                          return `₡${amount.toLocaleString('es-CR')}`;
                        })()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsStatusDialogOpen(false)}
              className="border border-white/20 text-white hover:bg-white/10 bg-transparent"
            >
              Volver
            </Button>
            <Button
              onClick={handleToggleMembershipStatus}
              disabled={updatingStatus}
              className={selectedMember?.membership?.is_active 
                ? 'bg-gradient-to-r from-red-500 to-red-600'
                : 'bg-gradient-to-r from-green-500 to-emerald-500'
              }
            >
              {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {selectedMember?.membership?.is_active ? 'Cancelar Membresía' : 'Reactivar Membresía'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Redeem Dialog */}
      <QuickRedeemDialog
        isOpen={isQuickRedeemOpen}
        onClose={() => {
          setIsQuickRedeemOpen(false);
          setQuickRedeemBenefit('');
        }}
        onConfirm={handleQuickRedeemConfirm}
        benefitName={
          selectedMember?.membership 
            ? monthlyBenefits[selectedMember.membership.plan]?.[quickRedeemBenefit]?.name || quickRedeemBenefit
            : quickRedeemBenefit
        }
        memberName={selectedMember?.profile.nombre || ''}
        isSubmitting={submitting}
      />

      {/* Renew Membership Dialog */}
      <RenewMembershipDialog
        isOpen={isRenewDialogOpen}
        onClose={() => setIsRenewDialogOpen(false)}
        onConfirm={handleRenewMembership}
        membership={selectedMember?.membership || null}
        memberName={selectedMember?.profile.nombre || ''}
        isSubmitting={renewingMembership}
      />

      {/* Activation Dialog */}
      {selectedMember && selectedMember.membership && (
        <ActivationDialog
          isOpen={isActivationDialogOpen}
          onClose={() => setIsActivationDialogOpen(false)}
          member={selectedMember}
          adminUserId={user?.id || ''}
          onActivated={() => {
            fetchAllMembers();
            // Update selected member with activated state
            if (selectedMember?.membership) {
              const updatedMember = {
                ...selectedMember,
                membership: {
                  ...selectedMember.membership,
                  is_active: true,
                  activated_at: new Date().toISOString(),
                  activated_by: user?.id || '',
                },
              };
              setSelectedMember(updatedMember);
            }
          }}
        />
      )}

      {/* Prize Winner Notification - Only for sellers */}
      {isVendedor && !isAdmin && showPrizeNotification && (
        <PrizeWinnerNotification
          prize={getClaimablePrize()}
          onClose={() => setShowPrizeNotification(false)}
          onClaim={() => {
            setShowPrizeNotification(false);
            setActiveTab('estadisticas');
            setRankingSubTab('premios');
          }}
          formatMonthYear={formatPrizeMonthYear}
        />
      )}


      {/* Fixed Bottom Tab Bar - Mobile Only - Always Visible via Portal */}
      {createPortal(
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 backdrop-blur-xl border-t border-white/10 px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] z-[9999] lg:hidden">
          <div className="max-w-7xl mx-auto">
            <div className={`grid ${isVendedor && !isAdmin ? 'grid-cols-4' : 'grid-cols-4'} gap-1 items-end`}>
              {/* Admin - Solo visible para admins */}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('acciones')}
                  className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl transition-all relative ${
                    activeTab === 'acciones' 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Bell className="w-5 h-5" />
                  <span className="text-xs">Admin</span>
                  {pendingActionsCount > 0 && (
                    <span className="absolute top-1 right-1/4 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold">
                      {pendingActionsCount}
                    </span>
                  )}
                </button>
              )}
              {/* Clientes - Visible para todos */}
              <button
                onClick={() => setActiveTab('miembros')}
                className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl transition-all ${
                  activeTab === 'miembros' 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs">Clientes</span>
              </button>
              {/* Equipo/Ranking - Cambia nombre según rol */}
              <button
                onClick={() => setActiveTab('estadisticas')}
                className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl transition-all ${
                  activeTab === 'estadisticas' 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {isVendedor && !isAdmin ? (
                  <Trophy className="w-5 h-5" />
                ) : (
                  <Medal className="w-5 h-5" />
                )}
                <span className="text-xs">{isVendedor && !isAdmin ? 'Ranking' : 'Equipo'}</span>
              </button>
              {/* Cursos - Solo visible para vendedores (junto a Actividad) */}
              {isVendedor && !isAdmin && (
                <button
                  onClick={() => setActiveTab('cursos')}
                  className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl transition-all relative ${
                    activeTab === 'cursos' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <GraduationCap className="w-5 h-5" />
                  <span className="text-xs">Cursos</span>
                  {pendingCoursesCount > 0 && (
                    <span className="absolute top-1 right-1/4 w-5 h-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-semibold">
                      {pendingCoursesCount}
                    </span>
                  )}
                </button>
              )}
              {/* Actividad - Visible para todos */}
              <button
                onClick={() => setActiveTab('actividad')}
                className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl transition-all ${
                  activeTab === 'actividad' 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <History className="w-5 h-5" />
                <span className="text-xs">Actividad</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Desktop Tab Bar */}
      <div className="hidden lg:block fixed top-4 left-1/2 -translate-x-1/2 bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-2xl px-2 py-1.5 z-50">
        <div className="flex items-center gap-1">
          {/* Admin - Solo visible para admins */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('acciones')}
              className={`flex items-center gap-2 py-2 px-4 rounded-xl transition-all relative ${
                activeTab === 'acciones' 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <Bell className="w-4 h-4" />
              <span className="text-sm">Admin</span>
              {pendingActionsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold">
                  {pendingActionsCount}
                </span>
              )}
            </button>
          )}
          {/* Clientes - Visible para todos */}
          <button
            onClick={() => setActiveTab('miembros')}
            className={`flex items-center gap-2 py-2 px-4 rounded-xl transition-all ${
              activeTab === 'miembros' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="text-sm">Clientes</span>
          </button>
          {/* Equipo/Ranking - Cambia nombre según rol */}
          <button
            onClick={() => setActiveTab('estadisticas')}
            className={`flex items-center gap-2 py-2 px-4 rounded-xl transition-all ${
              activeTab === 'estadisticas' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            {isVendedor && !isAdmin ? (
              <Trophy className="w-4 h-4" />
            ) : (
              <Medal className="w-4 h-4" />
            )}
            <span className="text-sm">{isVendedor && !isAdmin ? 'Ranking' : 'Equipo'}</span>
          </button>
          {/* Cursos - Solo visible para vendedores (junto a Actividad) */}
          {isVendedor && !isAdmin && (
            <button
              onClick={() => setActiveTab('cursos')}
              className={`flex items-center gap-2 py-2 px-4 rounded-xl transition-all relative ${
                activeTab === 'cursos' 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <GraduationCap className="w-4 h-4" />
              <span className="text-sm">Cursos</span>
              {pendingCoursesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-semibold">
                  {pendingCoursesCount}
                </span>
              )}
            </button>
          )}
          {/* Actividad - Visible para todos */}
          <button
            onClick={() => setActiveTab('actividad')}
            className={`flex items-center gap-2 py-2 px-4 rounded-xl transition-all ${
              activeTab === 'actividad' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <History className="w-4 h-4" />
            <span className="text-sm">Actividad</span>
          </button>
        </div>
      </div>
      
      {/* Achievement notification modal for sellers */}
      <AchievementUnlockedModal
        achievementType={newAchievement}
        onClose={dismissNewAchievement}
      />
    </div>
  );
};

export default AdminCanjes;
